import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { LanguageProvider } from './contexts/LanguageContext';

import Navbar from './components/layout/Navbar';
import StudentNavbar from './components/layout/StudentNavbar';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';
import EduVerifyBanner from './components/EduVerifyBanner';

import Home from './pages/Home';
import FurnitureListingPage from './pages/furniture/FurnitureListingPage';
import FurnitureDetail from './pages/furniture/FurnitureDetail';
import SellFurniture from './pages/furniture/SellFurniture';
import EditFurniture from './pages/furniture/EditFurniture';
import Login from './pages/auth/Login';
import PasswordReset from './pages/auth/PasswordReset';
import Register from './pages/auth/Register';
import { Navigate } from 'react-router-dom';
import BuyerDashboard from './pages/buyer/BuyerDashboard';
import SavedItems from './pages/buyer/SavedItems';
import SellerDashboard from './pages/seller/SellerDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import BuyerMessages from './pages/buyer/BuyerMessages';
import Terms from './pages/legal/Terms';
import Disclaimer from './pages/legal/Disclaimer';
import Safety from './pages/legal/Safety';
import Privacy from './pages/legal/Privacy';
import Profile from './pages/Profile';
import { MVP_ENABLE_CHECKOUT } from './constants/mvp';

const NavigationHandler: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();

  const authPages = ['/login', '/register', '/role-selection', '/forgot-password', '/reset-password'];
  const isAuthPage = authPages.includes(location.pathname);
  if (isAuthPage) {
    return null;
  }

  if (user && user.role !== 'admin') {
    return <StudentNavbar />;
  }

  return <Navbar />;
};

function AppContent() {
  return (
    <div className="flex w-full min-w-0 flex-col min-h-screen">
      <NavigationHandler />
      <EduVerifyBanner />
      <main className="w-full min-w-0 flex-grow">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/furniture" element={<FurnitureListingPage />} />
          <Route path="/furniture/:id" element={<FurnitureDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<PasswordReset />} />
          <Route path="/reset-password" element={<PasswordReset />} />
          <Route path="/register" element={<Register />} />
          <Route path="/role-selection" element={<Navigate to="/register" replace />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route path="/terms" element={<Terms />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="/privacy" element={<Privacy />} />

          <Route
            path="/buyer/dashboard"
            element={
              <ProtectedRoute>
                <BuyerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/saved"
            element={
              <ProtectedRoute>
                <SavedItems />
              </ProtectedRoute>
            }
          />
          <Route
            path="/buyer/messages"
            element={
              <ProtectedRoute>
                <BuyerMessages />
              </ProtectedRoute>
            }
          />

          <Route
            path="/seller/dashboard"
            element={
              <ProtectedRoute>
                <SellerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seller/messages"
            element={
              <ProtectedRoute>
                <BuyerMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sell"
            element={
              <ProtectedRoute>
                <SellFurniture />
              </ProtectedRoute>
            }
          />
          <Route
            path="/edit-furniture/:id"
            element={
              <ProtectedRoute>
                <EditFurniture />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <I18nextProvider i18n={i18n}>
        <LanguageProvider>
          <AuthProvider>
            {MVP_ENABLE_CHECKOUT ? (
              <CartProvider>
                <AppContent />
              </CartProvider>
            ) : (
              <CartProvider>
                <AppContent />
              </CartProvider>
            )}
          </AuthProvider>
        </LanguageProvider>
      </I18nextProvider>
    </Router>
  );
}

export default App;
